import { ApiError, post } from '../api';

/*
 * Real-time preview (owner, 18.10, item 16): the stream at the source's frame rate, straight
 * from the media node it is live on (docs/controlapi.yaml POST /api/live/view).
 *
 *   POST /api/live/view {stream, takeover} → {url} (a one-time ticket, 30 s)
 *   fetch(url) → frames: 1 byte type + 4 bytes big-endian length + payload
 *     I  init: 1 byte n, n bytes codec string, fMP4 init segment (ftyp + moov)
 *     F  one fMP4 fragment (moof + mdat) per frame
 *     E  the end: JSON {reason}; then the body closes
 *   → MediaSource → a <video> nobody sees; the previews on screen copy its frames.
 *
 * One viewer per stream, kept by the node: another window gets `busy` ("Start viewing"),
 * taking over ends the first with `taken_over` ("Viewing in another window").
 * `restart` (HOLD-A5): the node's logic was replaced in a planned update; the stream goes on,
 * so a new ticket is asked for at once and the picture comes back without a word on screen.
 * Video only, full source bitrate: "Low traffic" switches back to the keyframe preview.
 */

export type LiveViewState =
  | 'idle' // not asked
  | 'connecting'
  | 'playing'
  | 'busy' // someone else watches: "Start viewing"
  | 'taken' // another window took over: "Viewing in another window" + "Start viewing"
  | 'idle_timeout' // stopped after LIVE_IDLE_MS without activity
  | 'unavailable'; // the node has no live view, the browser no MSE, or it failed: keyframes

export interface LiveViewSnapshot {
  state: LiveViewState;
  /** frames per second actually shown, last second */
  fps?: number;
  /** why it is unavailable, for the title */
  why?: string;
}

/** frame types of the node's live view body */
export const FRAME_INIT = 0x49; // 'I'
export const FRAME_FRAGMENT = 0x46; // 'F'
export const FRAME_END = 0x45; // 'E'
/** a frame longer than this is a broken stream (4 MiB is the node's own cap for a keyframe) */
export const MAX_FRAME = 8 << 20;
/** no pointer, key or touch for this long: back to keyframes (owner, 18.10: 10 min) */
export const LIVE_IDLE_MS = 10 * 60_000;
/** `restart` ends (the node's logic replaced, HOLD-A5): this many new tickets within the window */
const RESTARTS_MAX = 3;
const RESTARTS_WINDOW_MS = 60_000;
/** keep the buffer short: real time, not smooth playback */
const MAX_BEHIND_S = 1.5;

export type Frame = { type: number; payload: Uint8Array };

/**
 * Splits the node's byte stream into frames. Feed it chunks as they come; it returns the
 * complete frames and keeps the rest. Throws on a frame of an unknown type or too long.
 */
export class FrameReader {
  private buf = new Uint8Array(0);

  push(chunk: Uint8Array): Frame[] {
    const b = new Uint8Array(this.buf.length + chunk.length);
    b.set(this.buf);
    b.set(chunk, this.buf.length);
    const out: Frame[] = [];
    let at = 0;
    while (b.length - at >= 5) {
      const type = b[at]!;
      if (type !== FRAME_INIT && type !== FRAME_FRAGMENT && type !== FRAME_END) throw new Error(`live view: frame type ${type}`);
      const len = new DataView(b.buffer, b.byteOffset + at + 1, 4).getUint32(0);
      if (len > MAX_FRAME) throw new Error(`live view: frame of ${len} bytes`);
      if (b.length - at - 5 < len) break;
      out.push({ type, payload: b.subarray(at + 5, at + 5 + len) });
      at += 5 + len;
    }
    this.buf = b.slice(at);
    return out;
  }
}

/* The cabinet's CSP lists the media servers it may fetch from (deploy/prod/Caddyfile,
 * connect-src): a node outside it is refused before any request leaves the browser. */
const blockedOrigins = new Set<string>();
if (typeof document !== 'undefined') {
  document.addEventListener('securitypolicyviolation', (e) => {
    try {
      blockedOrigins.add(new URL(e.blockedURI).origin);
    } catch {
      // "inline", "eval": not a fetch
    }
  });
}
function cspBlocked(url: string): boolean {
  try {
    return blockedOrigins.has(new URL(url).origin);
  } catch {
    return false;
  }
}
/** The init frame: the codec string and the init segment. */
export function parseInit(p: Uint8Array): { codec: string; init: Uint8Array } {
  const n = p[0];
  if (n === undefined || p.length < 1 + n) throw new Error('live view: short init');
  const codec = new TextDecoder().decode(p.subarray(1, 1 + n));
  if (!/^avc1\.[0-9A-Fa-f]{6}$/.test(codec)) throw new Error(`live view: codec ${codec}`);
  return { codec, init: p.subarray(1 + n) };
}

type MediaSourceCtor = typeof MediaSource;
function mediaSourceCtor(): MediaSourceCtor | undefined {
  const w = window as unknown as { MediaSource?: MediaSourceCtor; ManagedMediaSource?: MediaSourceCtor };
  return w.MediaSource ?? w.ManagedMediaSource;
}

export function liveViewSupported(): boolean {
  const M = mediaSourceCtor();
  try {
    return !!M && M.isTypeSupported('video/mp4; codecs="avc1.640028"');
  } catch {
    return false;
  }
}

/**
 * The live view of one stream. start() asks for a ticket and plays; stop() ends it.
 * Owned by the shared preview (previewHub), so a page change does not cut it.
 */
export class LiveView {
  readonly video: HTMLVideoElement;
  snap: LiveViewSnapshot = { state: 'idle' };
  private abort: AbortController | undefined;
  private ms: MediaSource | undefined;
  private sb: SourceBuffer | undefined;
  private codec = '';
  private queue: Uint8Array[] = [];
  private frames: number[] = [];
  private gen = 0;
  /** when the node ended the view with `restart` (a few in a row are fine, a loop is not) */
  private restarts: number[] = [];

  constructor(private stream: string, private onChange: (s: LiveViewSnapshot) => void) {
    // Shown as it is by the preview on screen (StreamPreview puts it in the page): the
    // browser's own size and frame timing, no copy through a canvas. No autoplay attribute:
    // Chrome pauses muted autoplay video it considers off screen; play() is called instead.
    const v = document.createElement('video');
    v.muted = true;
    v.playsInline = true;
    v.disablePictureInPicture = true;
    v.setAttribute('aria-hidden', 'true');
    v.style.cssText = 'display:block;width:100%;height:100%;object-fit:contain;background:transparent';
    this.video = v;
  }

  private set(p: Partial<LiveViewSnapshot>) {
    this.snap = { ...this.snap, ...p };
    this.onChange(this.snap);
  }

  /** a frame was shown: the fps figure */
  countFrame(t: number) {
    this.frames.push(t);
    while (this.frames.length && t - this.frames[0]! > 1000) this.frames.shift();
  }

  get fps(): number {
    return this.frames.length;
  }

  async start(takeover = false) {
    if (!liveViewSupported()) {
      this.set({ state: 'unavailable', why: "this browser can't play it" });
      return;
    }
    this.teardown();
    const gen = ++this.gen;
    this.set({ state: 'connecting', why: undefined });
    let url: string;
    try {
      ({ url } = await post<{ url: string; expires_at: string }>('/api/live/view', { stream: this.stream, takeover }));
    } catch (e) {
      if (gen !== this.gen) return;
      const code = e instanceof ApiError ? e.message : '';
      if (e instanceof ApiError && e.status === 409 && code === 'busy') this.set({ state: 'busy' });
      else if (e instanceof ApiError && e.status === 429) this.set({ state: 'idle' });
      else this.set({ state: 'unavailable', why: code === 'unsupported' ? 'the media server has no live view yet' : 'the media server did not answer' });
      return;
    }
    if (gen !== this.gen) return;
    void this.play(url, gen);
  }

  stop(state: LiveViewState = 'idle') {
    this.gen++;
    this.teardown();
    this.set({ state, fps: undefined });
  }

  private teardown() {
    this.abort?.abort();
    this.abort = undefined;
    this.queue = [];
    this.sb = undefined;
    this.codec = '';
    if (this.ms) {
      try {
        if (this.ms.readyState === 'open') this.ms.endOfStream();
      } catch {
        // already closed
      }
      this.ms = undefined;
    }
    if (this.video.src) {
      URL.revokeObjectURL(this.video.src);
      this.video.removeAttribute('src');
      this.video.load();
    }
    this.frames = [];
  }

  private async play(url: string, gen: number) {
    this.abort = new AbortController();
    let res: Response;
    try {
      res = await fetch(url, { cache: 'no-store', credentials: 'omit', signal: this.abort.signal });
    } catch {
      if (gen !== this.gen) return;
      // a fetch the page's CSP refused fails like a network error; the violation event says which
      await new Promise((ok) => setTimeout(ok, 0));
      this.set({ state: 'unavailable', why: cspBlocked(url) ? "this site's security settings block the media server" : "couldn't reach the media server" });
      return;
    }
    if (gen !== this.gen) return;
    if (!res.ok || !res.body) {
      const code = ((await res.json().catch(() => ({}))) as { error?: string }).error;
      if (gen !== this.gen) return;
      if (res.status === 409 && code === 'busy') this.set({ state: 'busy' });
      else this.set({ state: 'unavailable', why: `the media server said ${code ?? res.status}` });
      return;
    }
    const reader = res.body.getReader();
    const frames = new FrameReader();
    let reason: string | undefined;
    try {
      for (;;) {
        const { value, done } = await reader.read();
        if (done || gen !== this.gen) break;
        for (const f of frames.push(value)) {
          if (f.type === FRAME_END) {
            reason = ((JSON.parse(new TextDecoder().decode(f.payload)) as { reason?: string }).reason) ?? 'stream_ended';
            break;
          }
          if (f.type === FRAME_INIT) await this.onInit(f.payload);
          else this.append(f.payload);
          if (this.snap.state !== 'playing') this.set({ state: 'playing' });
        }
        if (reason) break;
      }
    } catch {
      reason = reason ?? 'broken';
    }
    if (gen !== this.gen) return;
    this.teardown();
    this.gen++;
    if (reason === 'restart' && this.restartAgain()) {
      void this.start();
      return;
    }
    if (reason === 'taken_over') this.set({ state: 'taken', fps: undefined });
    else if (reason === 'stream_ended' || reason === undefined) this.set({ state: 'idle', fps: undefined });
    else this.set({ state: 'unavailable', why: reason === 'too_slow' ? 'the connection is too slow for it' : 'the media server ended it', fps: undefined });
  }

  /** another start after `restart`: at most RESTARTS_MAX within RESTARTS_WINDOW_MS */
  private restartAgain(): boolean {
    const now = Date.now();
    this.restarts = this.restarts.filter((t) => now - t < RESTARTS_WINDOW_MS);
    if (this.restarts.length >= RESTARTS_MAX) return false;
    this.restarts.push(now);
    return true;
  }

  private async onInit(p: Uint8Array) {
    const { codec, init } = parseInit(p);
    if (!this.ms || codec !== this.codec) {
      this.teardownMedia();
      const M = mediaSourceCtor()!;
      const ms = new M();
      this.ms = ms;
      this.video.src = URL.createObjectURL(ms);
      await new Promise<void>((ok) => ms.addEventListener('sourceopen', () => ok(), { once: true }));
      this.sb = ms.addSourceBuffer(`video/mp4; codecs="${codec}"`);
      this.sb.mode = 'segments';
      this.sb.addEventListener('updateend', () => this.drain());
      this.codec = codec;
      void this.video.play().catch(() => undefined);
    }
    this.append(init);
  }

  private teardownMedia() {
    this.queue = [];
    this.sb = undefined;
    if (this.video.src) URL.revokeObjectURL(this.video.src);
  }

  private append(b: Uint8Array) {
    this.queue.push(b);
    this.drain();
  }

  private drain() {
    const sb = this.sb;
    if (!sb || sb.updating || !this.queue.length) return;
    const v = this.video;
    // Real time: when the buffer runs ahead, jump to its end and drop what is behind.
    if (sb.buffered.length) {
      const end = sb.buffered.end(sb.buffered.length - 1);
      if (end - v.currentTime > MAX_BEHIND_S) v.currentTime = Math.max(0, end - 0.2);
      const start = sb.buffered.start(0);
      if (v.currentTime - start > 10) {
        sb.remove(start, v.currentTime - 2);
        return;
      }
    }
    const next = this.queue.shift()!;
    try {
      sb.appendBuffer(next as BufferSource);
    } catch {
      this.queue = [];
    }
  }
}
